import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, getCookie, getAxios } from "@coremind/identity-core";
import { SignInPage, SignUpPage, VerifyEmailPage, ContactInvitePage } from "@coremind/identity-ui";
import DefaultTemplateLayout from "./Layout";
import { readRedirectTargetFromSearch, isChromeExtensionRedirectHostname } from "./authRouting";
import { HydraLoginRoute, HydraConsentRoute, takePendingHydraLogin } from "./hydra";
// Must be an absolute URL: this site (auth.offsetmind.com) has no "/apps" route of its own --
// a relative "/apps" here resolves against this host and 404s (root cause of the white screen
// reported visiting auth.offsetmind.com/ directly while already signed in, with no redirect_uri
// or oidc_request to act on instead).
//
// NOTE: this path is unrelated to the Matrix/Element X upstream-OIDC flow (that always carries
// oidc_request, handled separately above) -- it's only the generic "you're signed in, no specific
// destination" case. coremind-ui.offsetmind.com/apps was tried here and is wrong: that host is a
// stale, unmaintained S3 static site (last touched January) with its own disconnected old login
// UI, not the current one. The actively-developed apps/coremind/ui/app ("personal-dashboard")
// codebase that *would* be the real destination has no production deployment at all yet. Until
// that exists, fall back to the homepage rather than a stale/confusing screen.
const POST_LOGIN_FALLBACK_PATH = "https://www.offsetmind.com";
// Matches the sessionStorage key SignIn.tsx's Google button stashes this under before
// signInWithRedirect() navigates fully away -- Cognito's callback URL is fixed and drops
// whatever query string was on the page beforehand, so oidc_request would otherwise be lost
// across that round trip (see identity-ui/src/components/SignIn.tsx).
const PENDING_OIDC_REQUEST_STORAGE_KEY = "coremind:pending-oidc-request";
// Set by app/v1/routes/auth_oidc.py's /oauth2/authorize handler when it redirects an in-progress
// Matrix/Element X (or any other upstream-OIDC-federated) sign-in here to
// `/auth/signin?oidc_request=<jwt>`. Once the user is authenticated, this must be exchanged via
// POST /auth/oidc/authorize/complete for the client's redirect_uri+code -- it is NOT a normal
// post-login landing page and must never fall through to POST_LOGIN_FALLBACK_PATH.
const readPendingOidcRequest = (search) => {
    const fromUrl = new URLSearchParams(search).get("oidc_request");
    if (fromUrl)
        return fromUrl;
    if (typeof window === "undefined")
        return null;
    const stashed = window.sessionStorage.getItem(PENDING_OIDC_REQUEST_STORAGE_KEY);
    if (stashed) {
        window.sessionStorage.removeItem(PENDING_OIDC_REQUEST_STORAGE_KEY);
    }
    return stashed;
};
/**
 * The single central-auth host for the whole platform. Every other site redirects here
 * (`buildCentralAuthUrl` in each site's own authRouting.ts) with a `redirect_uri` query param
 * pointing back at itself; once signed in, this page sends the browser back there.
 *
 * That normally works with no tokens in the URL at all -- every other target is a
 * *.offsetmind.com site sharing the `accessToken`/`idToken` cookies AuthContext sets on
 * the `.offsetmind.com` domain (see identity-core/src/utils/cookies.ts). A Chrome
 * extension's chromiumapp.org redirect target can never receive that cookie (different
 * eTLD+1 entirely), so for that one case we hand the already-authenticated session's
 * tokens over in the URL *fragment* -- never sent to any server, same pattern Amplify's
 * own hosted-UI "token" response type already uses for browser clients with no secret.
 */
function SignInRoute() {
    const { session, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isAuthed = Boolean(session);
    React.useEffect(() => {
        if (loading || !isAuthed)
            return;
        const oidcRequest = readPendingOidcRequest(location.search);
        if (oidcRequest) {
            let cancelled = false;
            (async () => {
                try {
                    const idToken = getCookie("idToken") || undefined;
                    // Must hit this exact origin, not this site's configured apiBaseUrl (api.offsetmind.com,
                    // a different backend deployment) -- coremind-mgmt-api, only reachable via
                    // auth.offsetmind.com's own ingress route, is what signed oidc_request in the first
                    // place and is the only instance able to verify it. Passing an absolute URL overrides
                    // the axios instance's baseURL while keeping its auth-token request interceptor.
                    const { data } = await getAxios().post(`${window.location.origin}/api/v1/auth/oidc/authorize/complete`, {
                        request: oidcRequest,
                        id_token: idToken,
                    });
                    if (!cancelled && data?.redirect_to) {
                        window.location.replace(data.redirect_to);
                    }
                }
                catch (err) {
                    console.error("Failed to complete upstream OIDC authorization request:", err);
                }
            })();
            return () => {
                cancelled = true;
            };
        }
        // A Hydra sign-in (iOS app, MCP connectors, MAS) that went out to Google and came back without its
        // query string: resume it instead of falling through to the generic post-login redirect below.
        const pendingHydraLogin = takePendingHydraLogin();
        if (pendingHydraLogin) {
            navigate(`/auth/hydra/login?login_challenge=${encodeURIComponent(pendingHydraLogin)}`, { replace: true });
            return;
        }
        const target = readRedirectTargetFromSearch(location.search) || POST_LOGIN_FALLBACK_PATH;
        const targetUrl = (() => {
            try {
                return new URL(target, window.location.origin);
            }
            catch {
                return null;
            }
        })();
        if (targetUrl && isChromeExtensionRedirectHostname(targetUrl.hostname)) {
            const accessToken = getCookie("accessToken");
            const idToken = getCookie("idToken");
            if (!accessToken || !idToken) {
                // Shouldn't happen (isAuthed implies AuthContext just set these cookies),
                // but don't hand the extension a broken session if it does.
                return;
            }
            const params = new URLSearchParams({
                access_token: accessToken,
                id_token: idToken,
                token_type: "Bearer",
            });
            window.location.replace(`${targetUrl.origin}${targetUrl.pathname}#${params.toString()}`);
            return;
        }
        window.location.replace(target);
    }, [isAuthed, loading, location.search]);
    if (loading || isAuthed) {
        // Either still resolving the session, or authed and about to redirect away above --
        // render nothing rather than flash the sign-in form.
        return null;
    }
    return _jsx(SignInPage, { showSignupPrompt: true });
}
function RootRedirect() {
    const location = useLocation();
    return _jsx(Navigate, { to: `/auth/signin${location.search}`, replace: true });
}
export const authSiteAdapter = {
    siteId: "coremind-site-auth",
    version: "0.1.0",
    theme: {},
    layout: DefaultTemplateLayout,
    metadata: {
        name: "Offsetmind Sign In",
        description: "Central authentication for every Offsetmind site.",
    },
    navigation: { primary: [] },
    routes: [
        { path: "/", component: RootRedirect },
        { path: "/auth/signin", component: SignInRoute },
        { path: "/auth/hydra/login", component: HydraLoginRoute },
        { path: "/auth/hydra/consent", component: HydraConsentRoute },
        { path: "/auth/signup", component: SignUpPage },
        { path: "/auth/verify-email", component: VerifyEmailPage },
        { path: "/auth/contact-invite", component: ContactInvitePage },
    ],
};
